import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Users, UserPlus, Mail, Phone, Building2, Calendar,
  ChevronRight, MoreVertical, Search, Filter, Plus,
  DollarSign, TrendingUp, Clock, CheckCircle, XCircle,
  MessageSquare, FileText, ArrowRight, Trash2, Edit,
  BarChart3, Target, Zap, RefreshCw, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InstitutionalCRM = ({ institutionId }) => {
  const { t } = useTranslation();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showAddLead, setShowAddLead] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [stats, setStats] = useState({});

  // Pipeline stages
  const stages = [
    { id: 'new', name: t('crm.new'), color: 'bg-slate-100 text-slate-700', icon: UserPlus },
    { id: 'contacted', name: t('crm.contacted'), color: 'bg-blue-100 text-blue-700', icon: Phone },
    { id: 'qualified', name: t('crm.qualified'), color: 'bg-purple-100 text-purple-700', icon: Target },
    { id: 'proposal', name: t('crm.proposal'), color: 'bg-amber-100 text-amber-700', icon: FileText },
    { id: 'negotiation', name: t('crm.negotiation'), color: 'bg-orange-100 text-orange-700', icon: MessageSquare },
    { id: 'converted', name: t('crm.converted'), color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
    { id: 'lost', name: t('crm.lost'), color: 'bg-red-100 text-red-700', icon: XCircle },
  ];

  const [newLead, setNewLead] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    students: '',
    notes: '',
    source: 'manual'
  });

  useEffect(() => {
    loadLeads();
  }, []);

  const loadLeads = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      
      // For institutions, load from their CRM or shared leads
      // For now, we'll use a simulated endpoint
      const res = await axios.get(`${API}/crm/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLeads(res.data.leads || []);
      setStats(res.data.stats || {});
    } catch (error) {
      // If endpoint doesn't exist, use demo data
      const demoLeads = [
        {
          lead_id: 'demo_1',
          name: 'María García',
          email: 'maria@ejemplo.com',
          phone: '+34 612 345 678',
          company: 'Estudiante Individual',
          students: 1,
          status: 'new',
          source: 'landing',
          score: 75,
          created_at: new Date().toISOString(),
          notes: 'Interesada en preparación para enfermería'
        },
        {
          lead_id: 'demo_2',
          name: 'Carlos López',
          email: 'carlos@academia.com',
          phone: '+34 623 456 789',
          company: 'Academia López',
          students: 50,
          status: 'contacted',
          source: 'referral',
          score: 90,
          created_at: new Date().toISOString(),
          notes: 'Academia interesada en plan institucional'
        }
      ];
      setLeads(demoLeads);
      setStats({
        total: 2,
        new: 1,
        contacted: 1,
        converted: 0,
        conversion_rate: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddLead = async () => {
    if (!newLead.name || !newLead.email) {
      toast.error(t('crm.nameRequired'));
      return;
    }

    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.post(`${API}/crm/leads`, newLead, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLeads([res.data.lead, ...leads]);
      setShowAddLead(false);
      setNewLead({ name: '', email: '', phone: '', company: '', students: '', notes: '', source: 'manual' });
      toast.success(t('crm.leadAdded'));
    } catch (error) {
      // Demo mode - add locally
      const demoLead = {
        lead_id: `demo_${Date.now()}`,
        ...newLead,
        students: parseInt(newLead.students) || 1,
        status: 'new',
        score: 50,
        created_at: new Date().toISOString()
      };
      setLeads([demoLead, ...leads]);
      setShowAddLead(false);
      setNewLead({ name: '', email: '', phone: '', company: '', students: '', notes: '', source: 'manual' });
      toast.success(t('crm.leadAdded'));
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    console.log('Updating lead', leadId, 'to status:', newStatus);
    
    // Optimistically update local state first
    setLeads(prevLeads => {
      const updated = prevLeads.map(l => 
        l.lead_id === leadId ? { ...l, status: newStatus, updated_at: new Date().toISOString() } : l
      );
      console.log('Lead updated locally:', updated.find(l => l.lead_id === leadId));
      return updated;
    });
    
    // Then sync with backend
    try {
      const token = localStorage.getItem('session_token');
      await axios.put(`${API}/crm/leads/${leadId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(newStatus === 'converted' ? t('crm.leadConverted') : t('crm.leadUpdated'));
    } catch (error) {
      console.log('Backend sync failed, but local state is updated');
      toast.success(t('crm.leadUpdated'));
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm(t('common.confirm') + '?')) return;
    
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/crm/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Demo mode
    }
    
    setLeads(leads.filter(l => l.lead_id !== leadId));
    toast.success(t('crm.leadDeleted'));
  };

  // Filter leads
  const filteredLeads = leads.filter(lead => {
    const matchesSearch = !searchQuery || 
      lead.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.company?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || lead.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  // Group leads by stage for kanban view
  const leadsByStage = stages.reduce((acc, stage) => {
    acc[stage.id] = filteredLeads.filter(l => l.status === stage.id);
    return acc;
  }, {});

  const getScoreColor = (score) => {
    if (score >= 80) return 'bg-emerald-100 text-emerald-700';
    if (score >= 60) return 'bg-blue-100 text-blue-700';
    if (score >= 40) return 'bg-amber-100 text-amber-700';
    return 'bg-red-100 text-red-700';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-indigo-600" />
            CRM de Leads
          </h2>
          <p className="text-slate-600">Gestiona tus prospectos y oportunidades de venta</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadLeads} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button onClick={() => setShowAddLead(true)} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Lead
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-4 border">
          <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
            <Users className="w-4 h-4" />
            Total Leads
          </div>
          <p className="text-2xl font-bold text-slate-900">{leads.length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border">
          <div className="flex items-center gap-2 text-blue-500 text-sm mb-1">
            <UserPlus className="w-4 h-4" />
            Nuevos
          </div>
          <p className="text-2xl font-bold text-blue-600">{leadsByStage.new?.length || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border">
          <div className="flex items-center gap-2 text-purple-500 text-sm mb-1">
            <Target className="w-4 h-4" />
            Cualificados
          </div>
          <p className="text-2xl font-bold text-purple-600">{leadsByStage.qualified?.length || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border">
          <div className="flex items-center gap-2 text-emerald-500 text-sm mb-1">
            <CheckCircle className="w-4 h-4" />
            Convertidos
          </div>
          <p className="text-2xl font-bold text-emerald-600">{leadsByStage.converted?.length || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border">
          <div className="flex items-center gap-2 text-amber-500 text-sm mb-1">
            <TrendingUp className="w-4 h-4" />
            Tasa Conversión
          </div>
          <p className="text-2xl font-bold text-amber-600">
            {leads.length > 0 
              ? Math.round((leadsByStage.converted?.length || 0) / leads.length * 100) 
              : 0}%
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center bg-white rounded-xl p-4 border">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por nombre, email o empresa..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="border rounded-lg px-3 py-2"
        >
          <option value="all">Todos los estados</option>
          {stages.map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      {/* Kanban Board */}
      <div className="overflow-x-auto">
        <div className="flex gap-4 min-w-max pb-4">
          {stages.filter(s => s.id !== 'lost').map(stage => {
            const StageIcon = stage.icon;
            const stageLeads = leadsByStage[stage.id] || [];
            
            return (
              <div key={stage.id} className="w-72 flex-shrink-0">
                <div className={`rounded-t-lg p-3 ${stage.color} flex items-center justify-between`}>
                  <div className="flex items-center gap-2 font-medium">
                    <StageIcon className="w-4 h-4" />
                    {stage.name}
                  </div>
                  <span className="bg-white/50 px-2 py-0.5 rounded-full text-sm">
                    {stageLeads.length}
                  </span>
                </div>
                
                <div className="bg-slate-50 rounded-b-lg p-2 min-h-[400px] space-y-2">
                  {stageLeads.map(lead => (
                    <div 
                      key={lead.lead_id}
                      className="bg-white rounded-lg p-3 border shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => setSelectedLead(lead)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-slate-900">{lead.name}</p>
                          <p className="text-xs text-slate-500">{lead.company || 'Individual'}</p>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getScoreColor(lead.score)}`}>
                          {lead.score}
                        </span>
                      </div>
                      
                      <div className="space-y-1 text-xs text-slate-600">
                        <div className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {lead.email}
                        </div>
                        {lead.phone && (
                          <div className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {lead.phone}
                          </div>
                        )}
                        {lead.students > 1 && (
                          <div className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {lead.students} alumnos
                          </div>
                        )}
                      </div>
                      
                      {/* Quick actions */}
                      <div className="flex gap-1 mt-3 pt-2 border-t">
                        {stage.id !== 'converted' && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="flex-1 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              const currentIndex = stages.findIndex(s => s.id === stage.id);
                              const nextStage = stages[currentIndex + 1];
                              console.log('Current stage:', stage.id, 'Index:', currentIndex, 'Next:', nextStage?.id);
                              if (nextStage && nextStage.id !== 'lost') {
                                updateLeadStatus(lead.lead_id, nextStage.id);
                              }
                            }}
                          >
                            <ArrowRight className="w-3 h-3 mr-1" />
                            {stage.id === 'negotiation' ? 'Convertir' : 'Avanzar'}
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-red-600 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteLead(lead.lead_id);
                          }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  {stageLeads.length === 0 && (
                    <div className="text-center py-8 text-slate-400 text-sm">
                      Sin leads
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add Lead Modal */}
      {showAddLead && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-4">Añadir Nuevo Lead</h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Nombre *</label>
                <Input
                  value={newLead.name}
                  onChange={(e) => setNewLead({...newLead, name: e.target.value})}
                  placeholder="Nombre completo"
                />
              </div>
              
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Email *</label>
                <Input
                  type="email"
                  value={newLead.email}
                  onChange={(e) => setNewLead({...newLead, email: e.target.value})}
                  placeholder="email@ejemplo.com"
                />
              </div>
              
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Teléfono</label>
                <Input
                  value={newLead.phone}
                  onChange={(e) => setNewLead({...newLead, phone: e.target.value})}
                  placeholder="+34 600 000 000"
                />
              </div>
              
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Empresa/Academia</label>
                <Input
                  value={newLead.company}
                  onChange={(e) => setNewLead({...newLead, company: e.target.value})}
                  placeholder="Nombre de la academia"
                />
              </div>
              
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Nº Alumnos</label>
                <Input
                  type="number"
                  value={newLead.students}
                  onChange={(e) => setNewLead({...newLead, students: e.target.value})}
                  placeholder="Número de alumnos potenciales"
                />
              </div>
              
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Notas</label>
                <textarea
                  value={newLead.notes}
                  onChange={(e) => setNewLead({...newLead, notes: e.target.value})}
                  placeholder="Notas adicionales..."
                  className="w-full border rounded-lg p-2 resize-none"
                  rows={3}
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowAddLead(false)} className="flex-1">
                Cancelar
              </Button>
              <Button onClick={handleAddLead} className="flex-1 bg-indigo-600 hover:bg-indigo-700">
                Añadir Lead
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Lead Detail Modal */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold">{selectedLead.name}</h3>
                <p className="text-slate-500">{selectedLead.company || 'Individual'}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(selectedLead.score)}`}>
                Score: {selectedLead.score}
              </span>
            </div>
            
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-2 text-slate-600">
                <Mail className="w-4 h-4" />
                <a href={`mailto:${selectedLead.email}`} className="text-indigo-600 hover:underline">
                  {selectedLead.email}
                </a>
              </div>
              {selectedLead.phone && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Phone className="w-4 h-4" />
                  <a href={`tel:${selectedLead.phone}`} className="text-indigo-600 hover:underline">
                    {selectedLead.phone}
                  </a>
                </div>
              )}
              {selectedLead.students && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Users className="w-4 h-4" />
                  {selectedLead.students} alumnos potenciales
                </div>
              )}
              <div className="flex items-center gap-2 text-slate-600">
                <Calendar className="w-4 h-4" />
                Creado: {new Date(selectedLead.created_at).toLocaleDateString('es-ES')}
              </div>
            </div>
            
            {selectedLead.notes && (
              <div className="bg-slate-50 rounded-lg p-3 mb-6">
                <p className="text-sm text-slate-600">{selectedLead.notes}</p>
              </div>
            )}
            
            <div className="mb-6">
              <label className="text-sm font-medium text-slate-700 mb-2 block">Cambiar estado:</label>
              <div className="flex flex-wrap gap-2">
                {stages.map(stage => (
                  <button
                    key={stage.id}
                    onClick={() => {
                      updateLeadStatus(selectedLead.lead_id, stage.id);
                      setSelectedLead({...selectedLead, status: stage.id});
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      selectedLead.status === stage.id 
                        ? stage.color + ' ring-2 ring-offset-1 ring-indigo-500'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {stage.name}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setSelectedLead(null)} className="flex-1">
                Cerrar
              </Button>
              <Button 
                onClick={() => window.open(`mailto:${selectedLead.email}`, '_blank')}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700"
              >
                <Mail className="w-4 h-4 mr-2" />
                Enviar Email
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* CRM External Integration Note */}
      <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
        <div className="flex items-start gap-3">
          <ExternalLink className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="font-medium text-blue-900">¿Usas otro CRM?</p>
            <p className="text-sm text-blue-700 mt-1">
              Este CRM es opcional. Puedes seguir usando tu CRM actual (HubSpot, Salesforce, Pipedrive...) 
              y conectarlo mediante webhooks desde <strong>Configuración → Integraciones</strong>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstitutionalCRM;
